import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  message,
  Input,
  Select,
  Space,
  Row,
  Col,
  Upload,
} from "antd";
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import ProductDrawer from "./ProductDrawer";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import { getUserRole } from "../../utils/auth";

export default function Products() {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);

  const [loading, setLoading] = useState([]);
  const [stores, setStores] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    brand: "",
    category: "",
    store: "",
    search: "",
  });
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    setUserRole(getUserRole());
  }, []);

  const handleSearchChange = (filterName, value) => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        [filterName]: value ? value : null, // Ensure null is set if empty value
      };
      return updatedFilters;
    });
  };
  useEffect(() => {
    setPaginationInfo((prev) => ({ ...prev, currentPage: 1 }));
  }, [filters]); // Runs when filters change

  const fetchProducts = async (page = 1, pageSize = 20, filters = {}) => {
    try {
      setLoading(true);
      const { store, search, brand, category } = filters;
      const response = await API.get("/products/paginate", {
        params: {
          page,
          limit: pageSize,
          store,
          brand,
          category,
          search,
        },
      });
      const productsData = Array.isArray(response?.data?.products)
        ? response.data.products
        : [];
      setProducts(productsData);

      setPaginationInfo({
        currentPage: page,
        pageSize: pageSize,
        total: response?.data?.pagination?.totalProducts || 0,
      });
    } catch (error) {
      console.error("Error fetching products:", error.message);
      message.error("Failed to fetch products. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Fetch purchases when filters or pagination changes
    fetchProducts(paginationInfo.currentPage, paginationInfo.pageSize, filters);
  }, [filters, paginationInfo.currentPage, paginationInfo.pageSize]); // Re-fetch when filters or pagination change

  const handleCSVUpload = async (file) => {
    const isCSV = file.type === "text/csv";
    if (!isCSV) {
      message.error("You can only upload CSV files!");
      return false;
    }

    try {
      // Create a FormData object to append the file
      const formData = new FormData();
      formData.append("csvFile", file); // Use the key expected by your backend (`csvFile` in this case)

      // Send the file to the /csvImport route
      const response = await API.post("/products/csvImport", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        message.success("CSV file imported successfully!");
        // Trigger any state updates or refresh data if necessary
      }
    } catch (err) {
      console.error(err);
      message.error(err.response.data.message);
    }

    return false; // Prevent auto upload
  };

  const fetchData = async () => {
    try {
      const [storeResponse, brandResponse, categoryResponse] =
        await Promise.all([
          API.get("/stores"),
          API.get("/brands"),
          API.get("/category"),
        ]);
      setStores(storeResponse?.data || []);
      setBrands(brandResponse?.data || []);
      setCategories(categoryResponse?.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to fetch data.");
    }
  };

  const handleAddOrEditProduct = async (productData) => {
    try {
      fetchProducts(); // Refresh the product list
      setOpenDrawer(false);
    } catch (error) {
      console.error("Error saving product:", error);
      message.error(error.response.data.message);
    }
  };

  const handleDeleteProduct = async () => {
    try {
      await API.delete(`/products/${productToDelete._id}`);
      setProducts((prev) =>
        prev.filter((product) => product?._id !== productToDelete?._id)
      );
      message.success("Product deleted successfully!");
    } catch (error) {
      console.error("Error deleting product:", error);
      message.error(error.response?.data?.message);
    } finally {
      setIsDeleteModalVisible(false);
    }
  };

  const handleEditClick = (product) => {
    setSelectedProduct(product);
    setIsEdit(true);
    setOpenDrawer(true);
  };

  const handleAddClick = () => {
    setSelectedProduct(null);
    setIsEdit(false);
    setOpenDrawer(true);
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setIsDeleteModalVisible(true); // Show delete confirmation modal
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        (paginationInfo.currentPage - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Name",
      key: "name",
      render: (record) => <div>{record?.specifications?.name}</div>,
    },
    {
      title: "Category",
      key: "category",
      render: (record) => record?.category?.name || "N/A",
    },
    {
      title: "Brand",
      key: "brand",
      render: (record) => record?.brand?.name || "N/A",
    },
    {
      title: "Price (₹)",
      key: "price",
      render: (record) => {
        const { mrpPrice, salePrice } = record?.pricing || {};
        if (!mrpPrice) return "N/A";
        return (
          <span>
            <del style={{ color: "gray", marginRight: "8px" }}>₹{mrpPrice}</del>
            <b>₹{salePrice?.toFixed(2)}</b>
          </span>
        );
      },
    },
    {
      title: "Pur. Price (Inc GST)",
      key: "purchasePrice",
      render: (record) => {
        const { purchasePrice, taxes } = record?.pricing || {};
        if (!purchasePrice) return "N/A";
        return (
          // <span>
          //   <b
          //     style={{ color: "gray", marginRight: "8px" }}
          //   >{`₹${purchasePrice} (₹${(
          //     purchasePrice +
          //     purchasePrice * (taxes[0]?.percentage / 100)
          //   ).toFixed(2)})`}</b>
          // </span>

          <span>
            <b
              style={{ color: "gray", marginRight: "8px" }}
            >{`₹${(
              purchasePrice +
              purchasePrice * (taxes[0]?.percentage / 100)
            ).toFixed(2)}`}</b>
          </span>

          
        );
      },
    },
    {
      title: "Taxes",
      key: "taxes",
      render: (record) => {
        const { taxes } = record?.pricing || {};
        if (!taxes[0]?.percentage) return "N/A";
        return (
          <span>
            <b>{taxes[0]?.percentage?.toFixed(2) || 0} % </b>
          </span>
        );
      },
    },
    userRole.editPermission == true && {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (record) => (
        <div>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
            style={{ marginRight: 8 }}
          />
          {/* <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClick(record)}
          /> */}
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        padding: "20px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Row gutter={[5, 5]}>
          <Col xs={24} sm={6} md={4}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/productForm")}
            >
              Add New Product
            </Button>
          </Col>

          <Col xs={24} sm={10} md={6}>
            <Input
              placeholder="Search products"
              onChange={(e) => handleSearchChange("search", e.target.value)}
            />
          </Col>

          <Col xs={24} sm={6} md={4}>
            <Select
              style={{ width: "100%" }}
              placeholder="Brand"
              onChange={(value) => handleSearchChange("brand", value)}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {brands.map((brand) => (
                <Select.Option key={brand.id} value={brand.name}>
                  {brand.name}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={6} md={4}>
            <Select
              style={{ width: "100%" }}
              placeholder="Category"
              onChange={(value) => handleSearchChange("category", value)}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {categories.map((category) => (
                <Select.Option key={category.id} value={category.name}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={6} md={6}>
            <Select
              style={{ width: "100%" }}
              placeholder="Store"
              onChange={(value) => handleSearchChange("store", value)}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {stores.map((store) => (
                <Select.Option key={store.id} value={store.name}>
                  {store.name}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={24} md={24}>
            <CustomTable
              columns={columns}
              dataSource={products} // Ensure the filtered data is passed here
              onPaginationChange={(pagination) => {
                setPaginationInfo({
                  currentPage: pagination.current,
                  pageSize: pagination.pageSize,
                });
              }}
              loading={loading}
              style={{ padding: "0 0px", marginTop: "10px" }}
              total={paginationInfo.total}
            />
          </Col>
        </Row>

        {/* <Upload
            accept=".csv"
            customRequest={({ file }) => handleCSVUpload(file)}
            showUploadList={false}
          >
            <Button type="primary" icon={<UploadOutlined />} >Upload CSV</Button>
          </Upload> */}
      </div>

      {openDrawer && (
        <ProductDrawer
          open={openDrawer}
          setOpen={setOpenDrawer}
          isEdit={isEdit}
          initialValue={isEdit ? selectedProduct : null}
          onSubmit={handleAddOrEditProduct}
        />
      )}

      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteProduct}
        onCancel={() => setIsDeleteModalVisible(false)}
        title="Confirm Deletion"
        content="Are you sure you want to delete this product? This action cannot be undone."
      />
    </div>
  );
}
